import { memo } from "react";
import { Box, Skeleton, useTheme } from "@mui/material";
import { ResponsiveBar } from "@common/components/nivoCharts/NivoCharts";
import { DotLegalEmptyState } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { RiskLevelIndicator } from "../../../riskSettings/riskArea/riskAreaDialog/riskLevels/riskLevelIndicators";
import { RiskLevelLegend } from "../../../riskSettings/riskArea/riskAreaDialog/riskLevels/RiskLevelLegend";
import { chartContentHeight } from "../../riskManagementReport/RiskReportChartPanel";
import { riskChartTheme, riskChartTooltipContainerSx } from "../../riskChartStyles";
import { RiskDistributionHistoryResult } from "../RiskGovernanceOverview.types";
import { useRiskDistributionHistoryChart } from "./RiskDistributionHistoryChart.hooks";

interface RiskDistributionHistoryChartProps {
    isLoading: boolean;
    history: RiskDistributionHistoryResult | undefined;
    riskLevels: Array<string>;
    riskLevelIndicators: Array<RiskLevelIndicator>;
    asOf: string | null;
    isPdf?: boolean;
}

// memo'ed because the governance overview re-renders on every search keystroke, cell click and dialog toggle,
// and a nivo stack relayout is the most expensive bystander on the page; the props are referentially stable
// once the queries settle, so the boundary alone stops the re-renders.
const RiskDistributionHistoryChart = memo(function RiskDistributionHistoryChart({
    isLoading,
    history,
    riskLevels,
    riskLevelIndicators,
    asOf,
    isPdf,
}: RiskDistributionHistoryChartProps) {
    const theme = useTheme();
    const { translateString } = useTranslation();
    const points = history?.points ?? [];
    const scale = history?.scale ?? 0;
    const { chartData, keys, pointLabels, axisMax, tickValues, segmentColor, segmentTooltip, rotateTickLabels } = useRiskDistributionHistoryChart(
        points,
        scale,
        riskLevels,
        riskLevelIndicators,
        asOf
    );

    if (isLoading) {
        return <Skeleton variant="rounded" width="100%" height={chartContentHeight} />;
    }

    if (points.length === 0) {
        return (
            <Box sx={{ height: chartContentHeight, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <DotLegalEmptyState noFixedHeight text={translateString("noData")} />
            </Box>
        );
    }

    return (
        <>
            <Box sx={{ height: chartContentHeight, width: "100%" }}>
                <ResponsiveBar
                    data={chartData}
                    indexBy="index"
                    keys={keys}
                    colors={({ id }) => segmentColor(Number(id))}
                    maxValue={axisMax}
                    margin={{ top: 10, right: 20, bottom: rotateTickLabels ? 60 : 30, left: 40 }}
                    padding={0.3}
                    borderRadius={2}
                    enableLabel={false}
                    enableGridY
                    gridYValues={tickValues}
                    enableGridX={false}
                    isInteractive={!isPdf}
                    animate={!isPdf}
                    tooltip={({ id, value, data }) => {
                        const tooltip = segmentTooltip(Number(id), value, Number(data.total), Number(data.index));
                        return (
                            <Box sx={{ ...riskChartTooltipContainerSx(theme), padding: "6px 10px" }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 600 }}>
                                    <Box sx={{ width: 10, height: 10, borderRadius: "2px", backgroundColor: tooltip.color }} />
                                    {tooltip.label}
                                </Box>
                                <Box>{tooltip.share ? `${tooltip.count} (${tooltip.share})` : tooltip.count}</Box>
                                <Box sx={{ color: theme.palette.text.secondary }}>{tooltip.period}</Box>
                            </Box>
                        );
                    }}
                    axisLeft={{ tickValues, tickSize: 0, tickPadding: 8 }}
                    axisBottom={{
                        tickSize: 0,
                        tickPadding: 8,
                        tickRotation: rotateTickLabels ? -45 : 0,
                        format: (value) => pointLabels[Number(value)] ?? "",
                    }}
                    theme={riskChartTheme(theme)}
                />
            </Box>
            {/* The stacked segments are only named by their tooltip, and print has no hover, so the printed
                report carries the same legend the matrix does. */}
            {isPdf && <RiskLevelLegend riskLevels={riskLevels} riskLevelIndicators={riskLevelIndicators} />}
        </>
    );
});

export default RiskDistributionHistoryChart;
